import React, { useState, useEffect } from 'react';
import { documentApi, knowledgeEntryApi, Document, KnowledgeEntry } from './services/api';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Divider,
  TextField,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress
} from '@mui/material';

const ApiTest: React.FC = () => {
  // 状态
  const [documents, setDocuments] = useState<Document[]>([]);
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<KnowledgeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // 加载所有文档
  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const docs = await documentApi.getAllDocuments();
      setDocuments(docs);
      setSuccess('成功加载文档列表');
    } catch (err) {
      setError('加载文档失败: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // 加载所有知识条目
  const loadEntries = async () => {
    try {
      setLoading(true);
      setError(null);
      const loadedEntries = await knowledgeEntryApi.getAllEntries();
      setEntries(loadedEntries);
      setSuccess('成功加载知识条目');
    } catch (err) {
      setError('加载知识条目失败: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // 搜索知识
  const searchKnowledge = async () => {
    if (!searchQuery.trim()) {
      setError('请输入搜索关键词');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const results = await knowledgeEntryApi.searchSemantic(searchQuery);
      setSearchResults(results);
      setSuccess(`搜索完成，找到 ${results.length} 条结果`);
    } catch (err) {
      setError('搜索失败: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>API 接口测试</Typography>
      <Divider sx={{ mb: 3 }} />

      {loading && (
        <Box display="flex" justifyContent="center" my={2}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">文档管理</Typography>
              <Button variant="contained" onClick={loadDocuments}>
                加载所有文档
              </Button>
            </Box>
            
            <List>
              {documents.map(doc => (
                <ListItem key={doc.id} divider>
                  <ListItemText 
                    primary={doc.title} 
                    secondary={`${doc.category} | ${doc.fileType} | ${(doc.fileSize / 1024 / 1024).toFixed(2)}MB`} 
                  />
                </ListItem>
              ))}
              {documents.length === 0 && (
                <Typography color="text.secondary" align="center" py={2}>
                  暂无文档数据
                </Typography>
              )}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">知识条目</Typography>
              <Button variant="contained" onClick={loadEntries}>
                加载所有知识条目
              </Button>
            </Box>

            <List>
              {entries.map(entry => (
                <ListItem key={entry.id} divider>
                  <ListItemText 
                    primary={`来自文档: ${entry.document.title}`}
                    secondary={entry.content} 
                  />
                </ListItem>
              ))}
              {entries.length === 0 && (
                <Typography color="text.secondary" align="center" py={2}>
                  暂无知识条目数据
                </Typography>
              )}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>知识搜索测试</Typography>
            
            <Box display="flex" gap={2} mb={2}>
              <TextField
                fullWidth
                label="搜索关键词"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                variant="outlined"
              />
              <Button 
                variant="contained" 
                onClick={searchKnowledge}
                disabled={loading}
              >
                搜索
              </Button>
            </Box>

            <Typography variant="subtitle1" gutterBottom>
              搜索结果:
            </Typography>

            <List>
              {searchResults.map(result => (
                <ListItem key={result.id} divider>
                  <ListItemText 
                    primary={result.content} 
                    secondary={`文档: ${result.document.title}`} 
                  />
                </ListItem>
              ))}
              {searchResults.length === 0 && (
                <Typography color="text.secondary" align="center" py={2}>
                  暂无搜索结果
                </Typography>
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ApiTest; 